// Login Component
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Link,
  CircularProgress,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  Collapse,
} from '@mui/material';
import { ContentCut, Spa, Visibility, VisibilityOff, Email } from '@mui/icons-material';
import authService from '../../services/auth';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showResendOption, setShowResendOption] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState('');

  // Check for success message from registration
  useEffect(() => {
    if (location.state?.success && location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the state to prevent the message from persisting on refresh
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    
    // Clear success message when user starts typing
    if (successMessage) {
      setSuccessMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setShowResendOption(false);
    setResendSuccess('');

    try {
      const response = await authService.login(formData);
      
      // Check if user is Super Admin and redirect accordingly
      if (response.data?.user?.role_name === 'Super Admin') {
        navigate('/superadmin/dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.message || 'Login failed');
      
      // Check if this is an email verification error
      if (err.message && err.message.toLowerCase().includes('email')) {
        setShowResendOption(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    setResendLoading(true);
    setResendSuccess('');
    
    try {
      await authService.resendConfirmation(formData.email);
      setResendSuccess('Confirmation email sent successfully! Please check your inbox.');
      setShowResendOption(false);
    } catch (err) {
      setError('Failed to resend confirmation email: ' + err.message);
    } finally {
      setResendLoading(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #dc2cb0 0%, #E91E63 50%, #8E24AA 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 3,
      }}
    >
      <Container maxWidth="sm">
        <Card sx={{ boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: 4 }}>
            {/* Logo and Brand */}
            <Box textAlign="center" mb={4}>
              <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
                <ContentCut sx={{ fontSize: 40, color: 'primary.main', mr: 1 }} />
                <Spa sx={{ fontSize: 40, color: 'secondary.main' }} />
              </Box>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                Click<span style={{ color: '#dc2cb0' }}>Looks</span>
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Professional Salon Management System
              </Typography>
            </Box>

            {/* Login Form */}
            <form onSubmit={handleSubmit}>
              <Box mb={3}>
                <Typography variant="h5" gutterBottom textAlign="center">
                  Welcome Back
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
                  Sign in to your salon account
                </Typography>

                {successMessage && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {successMessage}
                  </Alert>
                )}

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                {/* Resend Confirmation Option */}
                <Collapse in={showResendOption}>
                  <Alert 
                    severity="info" 
                    sx={{ mb: 2 }}
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={handleResendConfirmation}
                        disabled={resendLoading}
                        startIcon={resendLoading ? <CircularProgress size={16} /> : <Email />}
                      >
                        {resendLoading ? 'Sending...' : 'Resend Email'}
                      </Button>
                    }
                  >
                    <Typography variant="body2">
                      <strong>Email not verified?</strong> Click the button to resend the confirmation email.
                    </Typography>
                  </Alert>
                </Collapse>

                {resendSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {resendSuccess}
                  </Alert>
                )}

                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  margin="normal"
                />

                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  margin="normal"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{ mt: 3, mb: 2, py: 1.5 }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Sign In'
                  )}
                </Button>

                <Box textAlign="center" mt={2}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => navigate('/forgot-password')}
                    sx={{ textDecoration: 'none', color: 'primary.main' }}
                  >
                    Forgot your password?
                  </Link>
                </Box>

                <Box textAlign="center" mt={2}>
                  <Typography variant="body2" color="text.secondary">
                    Don't have an account?{' '}
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => navigate('/register')}
                      sx={{ textDecoration: 'none' }}
                    >
                      Create a free salon account
                    </Link>
                  </Typography>
                </Box>
              </Box>
            </form>
 
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Login;
